import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Maps the current pathname to a document.title segment.
 * Returns the matched label (e.g. "Projects") or null for unmatched routes.
 *
 * Matching is segment-aware: deeper routes fall back to their root segment.
 * E.g. /projects/abc123/schedule → "Projects" (no separate label for
 * project-detail sub-routes by design — keeps the title list small).
 *
 * Hub routes are handled by web/src/hub/HubApp.tsx's own useDocumentTitle
 * call with a hub-specific suffix.
 */
const APP_TITLES = {
  // Auth + onboarding
  '/login':            'Sign in',
  '/signup':           'Sign up',
  '/forgot-password':  'Forgot password',
  '/reset-password':   'Reset password',
  '/verify-email':     'Verify email',
  '/onboarding':       'Welcome',

  // Top-level dashboards
  '/dashboard':        'Dashboard',
  '/projects':         'Projects',
  '/internal':         'Internal',
  '/tasks':            'Tasks',
  '/schedule':         'Schedule',
  '/contacts':         'Contacts',
  '/comms':            'Communications',
  '/estimates':        'Estimates',
  '/proposals':        'Proposals',
  '/invoices':         'Invoices',
  '/billing':          'Billing',
  '/purchase-orders':  'Purchase orders',
  '/rfis':             'RFIs',
  '/permits':          'Permits',
  '/decisions':        'Decisions',
  '/daily-logs':       'Daily logs',
  '/takeoffs':         'Takeoffs',
  '/cost-catalogue':   'Cost catalogue',
  '/consumables':      'Consumables',
  '/inventory':        'Inventory',
  '/agent-events':     'Agent events',
  '/board':            'Board',
  '/handbook':         'Handbook',
  '/settings':         'Settings',
  '/profile':          'Profile',
  '/scan':             'Scan',
  '/kiosk':            'Kiosk',

  // Legal
  '/privacy':          'Privacy',
  '/terms':            'Terms',

  // Hub routes (rendered by HubApp.tsx on hub.usetenon.com — same path map
  // works because the main app doesn't claim these. HubApp passes suffix="Hub".)
  '/overview':         'Overview',
  '/business':         'Business',
  '/sales':            'Sales',
  '/customers':        'Customers',
  '/tenants':          'Tenants',
  '/accounts':         'Accounts',
  '/agents':           'Agents',
  '/support':          'Support',
  '/engineering':      'Engineering',
  '/api-keys':         'API keys',
  '/auth/login':       'Sign in',
}

function labelForPath(pathname) {
  if (!pathname) return null
  if (APP_TITLES[pathname]) return APP_TITLES[pathname]
  // Try first-segment fallback (e.g. /projects/abc → 'Projects').
  const firstSeg = '/' + (pathname.split('/').filter(Boolean)[0] || '')
  return APP_TITLES[firstSeg] ?? null
}

/**
 * useDocumentTitle — sets `<title>` based on current route.
 * Mount once at the top of a router-aware component (App.jsx for the
 * main app, HubApp.tsx for the hub). Re-fires on every location change.
 *
 * @param suffix — appended after the route label (e.g. " · Hub"). Both
 *   the label and the suffix are joined to "Tenon" automatically.
 *   For unmatched routes the suffix-only fallback is used (e.g. "Tenon"
 *   on the main app, "Hub · Tenon" on the hub).
 */
export function useDocumentTitle(suffix = '') {
  const location = useLocation()
  useEffect(() => {
    const label = labelForPath(location.pathname)
    const parts = []
    if (label) parts.push(label)
    if (suffix) parts.push(suffix)
    parts.push('Tenon')
    document.title = parts.join(' · ')
  }, [location.pathname, suffix])
}
